import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable react/no-unescaped-entities */
/* eslint-disable max-lines */
import { Alert, Box, FormControlLabel, Switch } from '@mui/material';
import { addYears, isWithinInterval } from 'date-fns';
import format from 'date-fns/format';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { getHolidayLength } from '../../functions/getHolidayLength';
import { ReducerContext } from '../../hooks/reducer/reducerContext';
import { CalendarContext } from '../calendarContext';
import { NewBookingContext } from '../NewBookingContext';
import { Typography } from '../Typography';
function pluralize(word, count) {
    return count === 1 ? word : `${word}s`;
}
var ToggleDisplayMode;
(function (ToggleDisplayMode) {
    ToggleDisplayMode[ToggleDisplayMode["Active"] = 0] = "Active";
    ToggleDisplayMode[ToggleDisplayMode["Disabled"] = 1] = "Disabled";
    ToggleDisplayMode[ToggleDisplayMode["Hidden"] = 2] = "Hidden";
})(ToggleDisplayMode || (ToggleDisplayMode = {}));
export function NewHolidaySummary({ setIsSubmitButtonDisabled, onOpenManagerSettingsModalClick, OnShowManagerPrompt, }) {
    const { freeDays } = useContext(CalendarContext);
    const { userSettingsState } = useContext(ReducerContext);
    const { dateRange, startInAfternoon, endInMorning, updateState, deductFromEntitlement, isManagerApprovalRequired: sendForApproval, } = useContext(NewBookingContext);
    const [showManagerPrompt, setShowManagerPrompt] = useState(false);
    const [showPastManagerPrompt, setShowPastManagerPrompt] = useState(false);
    const [showPastAndUpcomingPrompt, setShowPastAndUpcomingPrompt] = useState(false);
    const [showHasManagerApprovalPrompt, setShowHasManagerApprovalPrompt] = useState(false);
    const [showFutureAbsencesMustDeductPrompt, setShowFutureAbsencesMustDeductPrompt] = useState(false);
    const [showFutureEntitlementYearDeductPrompt, setShowFutureEntitlementYearDeductPrompt,] = useState(false);
    const [showPreviousAndCurrentHolidayYearPrompt, setShowPreviousAndCurrentHolidayYearPrompt,] = useState(false);
    const [showCurrentAndFutureHolidayYearPrompt, setShowCurrentAndFutureHolidayYearPrompt,] = useState(false);
    const [showSendForApprovalRequiresDeductionPrompt, setShowSendForApprovalRequiresDeductionPrompt,] = useState(false);
    const [isInPreviousEntitlementYear, setIsInPreviousEntitlementYear] = useState(false);
    const [isInCurrentEntitlementYear, setIsInCurrentEntitlementYear] = useState(false);
    const [isInFutureEntitlementYear, setIsInFutureEntitlementYear] = useState(false);
    const [sendForApprovalDisplayMode, setSendForApprovalDisplayMode] = useState(ToggleDisplayMode.Active);
    const [deductionDisplayMode, setDeductionDisplayMode] = useState(ToggleDisplayMode.Active);
    const startDate = useMemo(() => dateRange.startDate?.toDate(), [dateRange.startDate]);
    const endDate = useMemo(() => dateRange.endDate?.toDate(), [dateRange.endDate]);
    const isInPast = useMemo(() => (endDate ?? startDate) < new Date(), [endDate, startDate]);
    const isSendApprovalInDisplayMode = (mode) => sendForApprovalDisplayMode === mode;
    const isDeductionInDisplayMode = (mode) => deductionDisplayMode === mode;
    useEffect(() => {
        const startOfCurrentHolidayYear = new Date(userSettingsState.start);
        const endOfCurrentHolidayYear = new Date(userSettingsState.end);
        const endOfLastHolidayYear = addYears(endOfCurrentHolidayYear, -1);
        setIsInPreviousEntitlementYear((endDate ?? startDate) < endOfLastHolidayYear);
        setIsInCurrentEntitlementYear(isWithinInterval(startDate, {
            start: startOfCurrentHolidayYear,
            end: endOfCurrentHolidayYear,
        }));
        setIsInFutureEntitlementYear((endDate ?? startDate) > endOfCurrentHolidayYear);
    }, [endDate, startDate, userSettingsState.end, userSettingsState.start]);
    // set defaults when selected date range changes
    useEffect(() => {
        if (isInPreviousEntitlementYear) {
            setSendForApprovalDisplayMode(ToggleDisplayMode.Hidden);
            setDeductionDisplayMode(ToggleDisplayMode.Hidden);
            updateState((prevState) => ({
                ...prevState,
                isManagerApprovalRequired: false,
                deductFromEntitlement: true,
            }));
        }
        else if (isInCurrentEntitlementYear && isInPast) {
            setDeductionDisplayMode(ToggleDisplayMode.Active);
            setSendForApprovalDisplayMode(ToggleDisplayMode.Active);
        }
        else if (isInCurrentEntitlementYear || isInFutureEntitlementYear) {
            updateState((prevState) => ({
                ...prevState,
                deductFromEntitlement: true,
            }));
            if (userSettingsState.managerEmail) {
                setSendForApprovalDisplayMode(ToggleDisplayMode.Hidden);
                updateState((prevState) => ({
                    ...prevState,
                    isManagerApprovalRequired: true,
                }));
            }
            setDeductionDisplayMode(ToggleDisplayMode.Hidden);
        }
        else if (isInPast) {
            setSendForApprovalDisplayMode(ToggleDisplayMode.Hidden);
            setDeductionDisplayMode(ToggleDisplayMode.Active);
            updateState((prevState) => ({
                ...prevState,
                isManagerApprovalRequired: false,
                deductFromEntitlement: false,
            }));
        }
        else {
            if (userSettingsState.managerEmail) {
                setSendForApprovalDisplayMode(ToggleDisplayMode.Hidden);
                updateState((prevState) => ({
                    ...prevState,
                    isManagerApprovalRequired: deductFromEntitlement,
                }));
            }
            setDeductionDisplayMode(ToggleDisplayMode.Active);
        }
        setShowHasManagerApprovalPrompt(false);
        setShowPastManagerPrompt(false);
        setShowSendForApprovalRequiresDeductionPrompt(false);
        setShowFutureAbsencesMustDeductPrompt(false);
        setShowFutureEntitlementYearDeductPrompt(false);
        const currentDate = new Date();
        const pastAndUpcoming = startDate < currentDate && endDate > currentDate;
        setShowPastAndUpcomingPrompt(pastAndUpcoming);
        const endOfCurrentHolidayYear = new Date(userSettingsState.end);
        const endOfLastHolidayYear = addYears(endOfCurrentHolidayYear, -1);
        const holidayStraddlesPastAndCurrentHolidayYears = startDate < endOfLastHolidayYear && endDate > endOfLastHolidayYear;
        setShowPreviousAndCurrentHolidayYearPrompt(holidayStraddlesPastAndCurrentHolidayYears);
        const holidayStraddlesCurrentAndFutureHolidayYears = startDate < endOfCurrentHolidayYear && endDate > endOfCurrentHolidayYear;
        setShowCurrentAndFutureHolidayYearPrompt(holidayStraddlesCurrentAndFutureHolidayYears);
        setIsSubmitButtonDisabled(pastAndUpcoming ||
            holidayStraddlesPastAndCurrentHolidayYears ||
            holidayStraddlesCurrentAndFutureHolidayYears);
    }, [
        dateRange,
        isInPreviousEntitlementYear,
        isInCurrentEntitlementYear,
        isInFutureEntitlementYear,
        isInPast,
        deductFromEntitlement,
        startDate,
        endDate,
        userSettingsState.managerEmail,
        setIsSubmitButtonDisabled,
        updateState,
        userSettingsState.end,
    ]);
    const isSendApprovalInDisabledMode = isSendApprovalInDisplayMode(ToggleDisplayMode.Disabled);
    const sendForApprovalClickHandler = useCallback(() => {
        if (!deductFromEntitlement) {
            setShowSendForApprovalRequiresDeductionPrompt(true);
        }
        if (deductFromEntitlement && userSettingsState.managerEmail && !isInPast) {
            setShowHasManagerApprovalPrompt(true);
        }
        if (isInPast && !isInCurrentEntitlementYear) {
            setShowPastManagerPrompt(true);
        }
        else if (!userSettingsState.managerEmail) {
            setShowManagerPrompt(true);
        }
        else if (!isSendApprovalInDisabledMode) {
            updateState((prevState) => ({
                ...prevState,
                isManagerApprovalRequired: !prevState.isManagerApprovalRequired,
            }));
        }
    }, [
        deductFromEntitlement,
        isInPast,
        isSendApprovalInDisabledMode,
        isInCurrentEntitlementYear,
        updateState,
        userSettingsState.managerEmail,
    ]);
    const deductFromEntitlementClickHandler = useCallback(() => {
        if (!isInPast) {
            if (sendForApproval && !userSettingsState.managerEmail) {
                setShowSendForApprovalRequiresDeductionPrompt(true);
            }
            if (isInCurrentEntitlementYear) {
                setShowFutureAbsencesMustDeductPrompt(true);
            }
            else if (isInFutureEntitlementYear) {
                setShowFutureEntitlementYearDeductPrompt(true);
            }
            else {
                updateState((prevState) => ({
                    ...prevState,
                    deductFromEntitlement: !prevState.deductFromEntitlement,
                    isManagerApprovalRequired: deductFromEntitlement
                        ? false
                        : prevState.isManagerApprovalRequired,
                }));
            }
        }
        else {
            if (isInCurrentEntitlementYear) {
                updateState((prevState) => ({
                    ...prevState,
                    deductFromEntitlement: !prevState.deductFromEntitlement,
                }));
            }
        }
    }, [
        deductFromEntitlement,
        isInCurrentEntitlementYear,
        isInFutureEntitlementYear,
        isInPast,
        sendForApproval,
        updateState,
        userSettingsState.managerEmail,
    ]);
    const managerPastCloseHandler = useCallback(() => setShowPastManagerPrompt(false), []);
    const hasManagagerForApprovalCloseHandler = useCallback(() => setShowHasManagerApprovalPrompt(false), []);
    const pastAndUpcomingCloseHandler = useCallback(() => setShowPastAndUpcomingPrompt(false), []);
    const previousAndCurrentHolidayYearCloseHandler = useCallback(() => setShowPreviousAndCurrentHolidayYearPrompt(false), []);
    const currentAndFutureHolidayYearCloseHandler = useCallback(() => setShowCurrentAndFutureHolidayYearPrompt(false), []);
    const sendForApprovalRequiresDeductionCloserHandler = useCallback(() => setShowSendForApprovalRequiresDeductionPrompt(false), []);
    const futureAbsencesMustDeductCloseHandler = useCallback(() => setShowFutureAbsencesMustDeductPrompt(false), []);
    const futureEntitlementYearDeductCloseHandler = useCallback(() => setShowFutureEntitlementYearDeductPrompt(false), []);
    const openManagerSettingsModal = useCallback(() => {
        onOpenManagerSettingsModalClick();
    }, [onOpenManagerSettingsModalClick]);
    if (!startDate) {
        return null;
    }
    const holidayLength = getHolidayLength(startDate, endDate ?? startDate, startInAfternoon, endInMorning, userSettingsState.workingDays, freeDays);
    return (_jsxs(_Fragment, { children: [_jsxs(Typography, { isLoading: false, children: [_jsxs("span", { children: [format(startDate, 'dd MMMM'), " "] }), !endDate ? null : (_jsxs(_Fragment, { children: [_jsx(FaArrowRight, { style: { marginBottom: '-2px' } }), ' ', _jsxs("span", { children: [format(endDate, 'dd MMMM'), " "] })] })), "(", holidayLength, " ", pluralize('day', holidayLength), ")"] }), !isDeductionInDisplayMode(ToggleDisplayMode.Hidden) ? (_jsxs(Typography, { isLoading: false, children: [_jsx(Box, { component: "span", "data-testid": 'deduct-from-entitlement-box', onClick: isDeductionInDisplayMode(ToggleDisplayMode.Disabled)
                            ? deductFromEntitlementClickHandler
                            : undefined, children: _jsx(FormControlLabel, { control: _jsx(Switch, { "data-testid": 'deduct-from-entitlement-switch', checked: deductFromEntitlement, onChange: deductFromEntitlementClickHandler, sx: { mr: '2rem' } }), label: "Should this absence deduct from your entitlement?", labelPlacement: "start", disabled: isDeductionInDisplayMode(ToggleDisplayMode.Disabled) }) }), !showSendForApprovalRequiresDeductionPrompt ? null : (_jsx(Alert, { severity: "error", onClose: sendForApprovalRequiresDeductionCloserHandler, children: "To send an approval request, your absence must deduct from your entitlement." })), !showHasManagerApprovalPrompt ? null : (_jsx(Alert, { severity: "info", onClose: hasManagagerForApprovalCloseHandler, children: "You have a manager set up to receive requests." })), !showFutureAbsencesMustDeductPrompt ? null : (_jsx(Alert, { severity: "info", onClose: futureAbsencesMustDeductCloseHandler, children: "Future absences must deduct from your entitlement." })), !showFutureEntitlementYearDeductPrompt ? null : (_jsx(Alert, { severity: "info", onClose: futureEntitlementYearDeductCloseHandler, children: "Future absences must deduct from entitlement; your current year's entitlement will be unaffected." }))] })) : null, !isSendApprovalInDisplayMode(ToggleDisplayMode.Hidden) ? (_jsxs(Typography, { isLoading: false, children: [_jsx(Box, { component: "span", "data-testid": 'send-for-approval-box', onClick: isSendApprovalInDisplayMode(ToggleDisplayMode.Disabled)
                            ? sendForApprovalClickHandler
                            : undefined, children: _jsx(FormControlLabel, { control: _jsx(Switch, { "data-testid": 'send-for-approval-switch', checked: sendForApproval, onChange: sendForApprovalClickHandler }), label: "Send for approval?", labelPlacement: "start", disabled: isSendApprovalInDisplayMode(ToggleDisplayMode.Disabled) }) }), showManagerPrompt ? (_jsx(OnShowManagerPrompt, { onOpenManagerSettingsModal: openManagerSettingsModal })) : showPastManagerPrompt ? (_jsx(Alert, { severity: "error", onClose: managerPastCloseHandler, children: "To send an approval request, your absence must occur in the future." })) : null] })) : null, !showPastAndUpcomingPrompt ? null : (_jsx(Alert, { severity: "error", onClose: pastAndUpcomingCloseHandler, children: "Please split this absence into past and upcoming." })), showPreviousAndCurrentHolidayYearPrompt && (_jsx(Alert, { severity: "error", onClose: previousAndCurrentHolidayYearCloseHandler, children: "Please split this absence into previous and current holiday years." })), showCurrentAndFutureHolidayYearPrompt && (_jsx(Alert, { severity: "error", onClose: currentAndFutureHolidayYearCloseHandler, children: "Please split this absence into current and future holiday years." }))] }));
}
